import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, Observer, of } from 'rxjs';
import { debounceTime, filter, mergeMap } from 'rxjs/operators';

import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { SSE_EVENT_TYPES } from '../../services/closeout/sse-event-types';
import GlobalService from '../../services/Global.service';
import { processErrorInResponse, processResponse } from '../helpers';
import { getAccountPendingOrdersSuccess, getOpenPositionsSuccess } from '../reporting';
import { getCurrentAccount } from '../crm/helpers';
import { RootState } from '..';

import { fetchCloseoutStatusOnLastClose, getAccountCloseoutStatus, getAccountCloseoutStatusCompleted, getAccountCloseoutStatusFailed, sseEventReceived } from './index';

const getAccountCloseoutStatusEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getAccountCloseoutStatus.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    GlobalService
      .closeoutService
      .getAccountCloseoutStatus(action.payload)
      .then(response => {
        processResponse(
          getAccountCloseoutStatus.type,
          {},
          response,
          observer,
          getAccountCloseoutStatusCompleted,
          getAccountCloseoutStatusFailed,
        );
      })
      .catch(error => {
        processErrorInResponse(getAccountCloseoutStatus.type, error, action, observer, getAccountCloseoutStatusFailed);
      });
  })),
);

const refetchOpenPositionsEpic = (action$: Observable<any>) => action$.pipe(
  ofType(sseEventReceived.type),
  filter(action => {
    const event = action.payload;
    return (
      (event?.type === SSE_EVENT_TYPES.ACCOUNT_STATUS_UPDATE && event?.data?.dailyResetEvent?.isCompleted === true)
      || event?.type === SSE_EVENT_TYPES.DAILY_AUTO_CLOSE
      || event?.type === SSE_EVENT_TYPES.DAILY_LOSS_REACHED
      || event?.type === SSE_EVENT_TYPES.MAX_LOSS_REACHED
      || event?.type === SSE_EVENT_TYPES.PROFIT_TARGET_REACHED
    );
  }),
  mergeMap(() => of(
    getOpenPositionsSuccess({ accounts: [] }),
    getAccountPendingOrdersSuccess({ accounts: [] }),
  )),
);

const refetchCloseoutStatusEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(sseEventReceived.type),
  filter(action => action.payload?.type === SSE_EVENT_TYPES.ACCOUNT_STATUS_UPDATE
    && action.payload?.data?.dailyResetEvent?.isCompleted === true),
  debounceTime(PredefinedMilisecondsTypeEnum.halfSecond),
  mergeMap(() => {
    const accountReferenceId = getCurrentAccount(state$.value);
    return of(getAccountCloseoutStatus(accountReferenceId));
  }),
);

const fetchCloseoutStatusOnLastCloseEpic = (action$: Observable<any>) => action$.pipe(
  ofType(fetchCloseoutStatusOnLastClose.type),
  debounceTime(PredefinedMilisecondsTypeEnum.halfSecond),
  mergeMap(action => of(getAccountCloseoutStatus(action.payload))),
);

export default combineEpics(
  getAccountCloseoutStatusEpic,
  refetchOpenPositionsEpic,
  refetchCloseoutStatusEpic,
  fetchCloseoutStatusOnLastCloseEpic,
);
